import classNames from "classnames";
import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import {
  documentViewableGlobPatterns,
  isDocumentViewable,
} from "@/shared/domain/files/fileTypes";
import { Button } from "@/shared/foundation/primitives";
import { usePopupState } from "@/shared/hooks";
import {
  DocumentPanelState,
  actions,
  useVizDispatch,
} from "@/shared/state/visualization";

import { FilePicker } from "../../../FilePicker";
import { useActiveDatasetIds } from "../../hooks/useActiveDatasetIds";
import { useAddFilesToWorkspace } from "../../hooks/useAddFilesToWorkspace";
import { type PanelSettingsSidebarProps } from "../../sidebarContext";
import { useWorkspaceFiles } from "../../WorkspaceCtx";
import { useEphemeralWorkspaceState } from "../../WorkspaceCtx/hooks";
import { BasePanelSettings } from "../BasePanelSettings";
import { SettingsSection } from "../SettingsSection";

import styles from "./DocumentPanelSettings.module.css";

interface DocumentPanelSettingsProps extends PanelSettingsSidebarProps {
  state: DocumentPanelState;
}

export function DocumentPanelSettings({ state }: DocumentPanelSettingsProps) {
  const dispatch = useVizDispatch();
  const orgId = useCurrentOrgId();
  const popup = usePopupState();
  const { addFiles } = useAddFilesToWorkspace();
  const { files } = useEphemeralWorkspaceState();
  const workspaceFiles = useWorkspaceFiles();
  const activeDatasetIds = useActiveDatasetIds();

  // Filter workspace files to viewable documents (HTML, PDF, JSON, YAML, XML)
  const documentFiles = React.useMemo(
    () => workspaceFiles.filter((f) => isDocumentViewable(f.relative_path)),
    [workspaceFiles],
  );

  const handleSelectFile = React.useCallback(
    (file: { file_id: string; relative_path: string }) => {
      dispatch(
        actions.setDocumentPanelFile(state.id, {
          fileId: file.file_id,
          fileName: file.relative_path,
        }),
      );
    },
    [dispatch, state.id],
  );

  const handleAddFiles = (selectedFileIds: string[]) => {
    addFiles(selectedFileIds);
    popup.close();
  };

  return (
    <BasePanelSettings state={state}>
      <SettingsSection title="Document">
        {documentFiles.length === 0 && (
          <div className={styles["empty-state"]}>
            <p className={styles["no-files"]}>
              No viewable documents found in this workspace. Supported formats:
              HTML, PDF, JSON, YAML, XML. Add files from a dataset to get
              started.
            </p>
            <Button
              variant="outlined"
              size="small"
              onClick={() => popup.open()}
            >
              Add files to workspace
            </Button>
          </div>
        )}

        {documentFiles.length > 0 && (
          <>
            <ul className={styles["file-list"]}>
              {documentFiles.map((file) => (
                <li key={file.file_id}>
                  <button
                    type="button"
                    className={classNames(styles["file-item"], {
                      [styles["file-item-selected"]]:
                        state.data?.fileId === file.file_id,
                    })}
                    onClick={() => handleSelectFile(file)}
                  >
                    {file.relative_path}
                  </button>
                </li>
              ))}
            </ul>
            <Button
              className={styles["browse-button"]}
              variant="text"
              size="small"
              onClick={() => popup.open()}
            >
              Add files to workspace
            </Button>
          </>
        )}
      </SettingsSection>

      <FilePicker
        orgId={orgId}
        open={popup.isOpen}
        onClose={popup.close}
        onSubmit={(selectedFiles) =>
          handleAddFiles(selectedFiles.map((f) => f.file_id))
        }
        title="Add files to workspace"
        activeDatasetIds={activeDatasetIds}
        existingFileIds={files.map((file) => file.file_id)}
        includePatterns={documentViewableGlobPatterns}
      />
    </BasePanelSettings>
  );
}
